import React, { SyntheticEvent } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import { CookingTimeText } from '../CookingTimeText'
import { RecipeGridRecipeTileImage as TileImage } from '@library/menu-page'
import { Title } from '../Title'
import { DietaryLabels } from '../DietaryLabels'
import { LabelWrapper } from '../Label/LabelWrapper'
import { SoldOutOverlay } from '../SoldOutOverlay'
import { CaloriesText } from '../Calories'
import { CollectionUUID, Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { AddRecipeButton } from '../AddRecipeButton'
import { RecipeTilePurchaseInfo } from '../RecipeTilePurchaseInfo'
import { RecipeTileReadyToHeatBanner } from '../RecipeTileReadyToHeatBanner'
import { cssAddRecipeButtonContainer, cssRecipeTagHolder } from '../RecipeTile/styles'
import { ProteinText } from '../ProteinText'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'

const RecipeTagContainer = styled.span(() => ({
  ...cssRecipeTagHolder,
}))

const ImageContainer = withCitrusTheme(Box, () => ({
  minHeight: '200px',
  maxHeight: '270px',
  position: 'relative',
  overflow: 'hidden',
}))

const AddRecipeButtonContainer = styled.span(() => ({
  ...cssAddRecipeButtonContainer,
}))

export type RecipeTileHeaderProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  menuPreferences?: MenuPreferences
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe?: (recipeId: RecipeUUID) => void
  removeRecipe?: (recipeId: RecipeCoreId) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  index?: number
  alternativeOptions?: AlternativeOptions
  lazy?: boolean
  insideOverflow?: boolean
  showPurchaseInfo?: boolean
  numPortions: number
}

export const RecipeTileHeader = ({
  recipe,
  currentCollectionId: categoryId,
  menuPreferences,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  index,
  lazy = true,
  insideOverflow = false,
  showPurchaseInfo = true,
  numPortions,
  alternativeOptions,
}: RecipeTileHeaderProps) => {
  const isHeatOnly = recipe.preparationType === 'heat-only'
  const hasAlternativeOptions =
    alternativeOptions?.optionsType !== 'none' && alternativeOptions?.optionsType !== undefined

  const showAddButton = addRecipe && removeRecipe
  const showCaloriesText = isCaloriePreferenceSelected(menuPreferences?.display?.list)

  const handleOnClickRecipeButtonContainer = (e: SyntheticEvent) => {
    // Prevents opening recipe details modal when clicking on disabled add button
    if (isOutOfStock) e.stopPropagation()
  }

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  return (
    <Box
      width="100%"
      height="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      position={DisplayPosition.Relative}
    >
      <ImageContainer>
        <SoldOutOverlay isOutOfStock={isOutOfStock} />
        <TileImage lazy={lazy} recipe={recipe} insideOverflow={insideOverflow} decorative />
        <RecipeTagContainer>
          <DietaryLabels recipe={recipe} />
        </RecipeTagContainer>
      </ImageContainer>

      <Box position={DisplayPosition.Relative} flexGrow={1} style={{ margin: '1rem' }}>
        <Box display={Display.Flex} paddingBottom={2}>
          <LabelWrapper
            isOutOfStock={isOutOfStock}
            recipe={recipe}
            reserveCtaSpace={Boolean(showAddButton)}
          />
          {showAddButton && (
            <AddRecipeButtonContainer onClick={handleOnClickRecipeButtonContainer}>
              <AddRecipeButton
                recipeId={recipe.coreId}
                recipeUuid={recipe.id}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipe}
                removeRecipe={removeRecipe}
                reachedLimit={reachedLimit}
                isInBasket={isInBasket}
                isOutOfStock={isOutOfStock}
                index={index}
              />
            </AddRecipeButtonContainer>
          )}
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          justifyContent={JustifyContent.SpaceBetween}
          height="100%"
          position={DisplayPosition.Relative}
        >
          <SoldOutOverlay backgroundColor={Color.White} isOutOfStock={isOutOfStock} />
          <Box>
            <Title title={recipe.name} />
            <Space size={2}></Space>
            <Box display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
              <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1} wrap>
                <CookingTimeText recipe={recipe} numPortions={numPortions} />
                {isHeatOnly && <ProteinText recipe={recipe} />}
                {showCaloriesText && <CaloriesText recipe={recipe} />}
              </Box>
              {hasAlternativeOptions && (
                <Text fontWeight={FontWeight.Bold} style={{ visibility: 'hidden' }}>
                  Customise
                </Text>
              )}
            </Box>
          </Box>
          {showPurchaseInfo && (
            <RecipeTilePurchaseInfo isOutOfStock={isOutOfStock} recipe={recipe} />
          )}
        </Box>
      </Box>
      <RecipeTileReadyToHeatBanner recipe={recipe} isOutOfStock={isOutOfStock} />
    </Box>
  )
}
